import { Button, Checkbox, Skeleton } from "@/components/atoms";
import { Divider } from "@/components/atoms/Divider";
import { Typography } from "@/components/atoms/Typography";
import { DirhamIcon } from "@/components/atoms/icons";
import { FullScreenLoader } from "@/components/molecules/FullScreenLoader";
import { OkDialog } from "@/components/molecules/dialogs/OkDialog";
import { TextHeader } from "@/components/molecules/header/TextHeader";
import { useTranslation, useUIColors } from "@/core/hooks";
import { logger } from "@/core/logger";
import { useI18nContext } from "@/core/providers/I18nProvider";
import { DT_SCREEN } from "@/core/constants/dynatraceScreens";
import { trackDynatraceEvent } from "@/core/services/dynatraceService";
import { trackCatalogEvent } from "@/core/services/trackDynatraceCatalog";
import { fetchTNCVersion } from "@/core/services/legalDocumentsService";
import { useAppStore } from "@/core/stores/appStore";
import { generatePaymentHmacSignature } from "@/core/utils/hmac";
import { router, useLocalSearchParams } from "expo-router";
import React, { useEffect, useMemo, useState } from "react";
import {
  Text as RNText,
  Platform,
  ScrollView,
  StyleSheet,
  View,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { usePaymentSummary } from "./hooks/usePaymentSummary";
import { paymentService, type CreateCheckoutRequest } from "./services/paymentService";

export interface PaymentSummaryScreenProps {
  selectedPaymentIds?: string[];
  onBackPress?: () => void;
  onSupportPress?: () => void;
  onPayNowPress?: () => void;
  useCloseIcon?: boolean;
}

export default function PaymentSummaryScreen({
  onBackPress,
  onSupportPress,
  onPayNowPress,
  useCloseIcon = false,
}: PaymentSummaryScreenProps) {
  const colors = useUIColors();
  const insets = useSafeAreaInsets();
  const { t } = useTranslation();
  const { isRTL } = useI18nContext();
  const params = useLocalSearchParams<{
    selectedPaymentIds?: string;
    tncAccepted?: string;
  }>();
  const unitInstallments = useAppStore((state) => state.unitInstallments);

  // Get selected payment IDs from URL params or props
  const selectedPaymentIds = useMemo(() => {
    if (params.selectedPaymentIds) {
      return params.selectedPaymentIds.split(",").filter(Boolean);
    }
    return [];
  }, [params.selectedPaymentIds]);

  // Fetch payment data and fees
  const {
    selectedPayments,
    installments,
    charges,
    feesData,
    isLoadingFees,
    latePaymentFee,
    convenienceFee,
    totalBeforeCardFee,
    // domesticCardFee,
    // internationalCardFee,
  } = usePaymentSummary(selectedPaymentIds);

  const handleBackPress = () => {
    if (onBackPress) {
      onBackPress();
    } else {
      router.back();
    }
  };

  const handleSupportPress = () => {
    if (onSupportPress) {
      onSupportPress();
    } else {
      router.push("/service-request" as any);
    }
  };

  const [isProcessingCheckout, setIsProcessingCheckout] = useState(false);
  const [errorDialogVisible, setErrorDialogVisible] = useState(false);
  const [errorDialogMessage, setErrorDialogMessage] = useState("");
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const [isLoadingTNCVersions, setIsLoadingTNCVersions] = useState(true);
  const [contentfulTNCVersion, setContentfulTNCVersion] = useState<
    string | null
  >(null);
  const [backendTNCVersion, setBackendTNCVersion] = useState<string | null>(
    null,
  );

  useEffect(() => {
    trackDynatraceEvent({
      p_screen: "Payment Summary",
      p_actionName: "Payment Summary Viewed",
      eventType: "view",
    });
  }, []);

  const handlePayNowPress = async () => {
    trackCatalogEvent({
      screen: DT_SCREEN.PAYMENT_SUMMARY,
      actionName: "payment_summary_pay_now_click",
      eventType: "action",
    });
    if (onPayNowPress) {
      onPayNowPress();
      return;
    }

    if (selectedPayments.length === 0) {
      setErrorDialogMessage(t("payments.noPaymentsSelected"));
      setErrorDialogVisible(true);
      return;
    }

    try {
      setIsProcessingCheckout(true);

      // Build splits - only include items with MID
      const paymentsWithMid = selectedPayments.filter(
        (p) => p.mid != null && p.mid !== "",
      );
      const paymentsWithoutMid = selectedPayments.filter(
        (p) => !p.mid || p.mid === "",
      );

      // Check if any payment has no MID
      if (paymentsWithoutMid.length > 0) {
        logger.error("PaymentSummaryScreen:Some payments have no MID", {
          paymentsWithoutMid: paymentsWithoutMid.map((p) => ({
            id: p.id,
            title: p.title,
          })),
        });

        // Get payment names (titles) for payments without MID
        const paymentNames = paymentsWithoutMid.map((p) => p.title).join(", ");
        const errorMessage = t("payments.paymentTemporaryIssue").replace(
          "{{paymentNames}}",
          paymentNames,
        );
        setErrorDialogMessage(errorMessage);
        setErrorDialogVisible(true);
        setIsProcessingCheckout(false);
        return;
      }

      // Build splits - get revenue_mid from each payment's unit metadata
      const splits = paymentsWithMid.map((p) => {
        // Get propertyId from payment's originalItem
        const propertyId =
          (p.originalItem as any)?.propertyId ||
          (p.originalItem as any)?.property_id;

        // Get revenue_MID from unit installments metadata for this specific unit
        // This comes from the GET /v1/customers/unit-installments API response metadata object
        let revenueMidValue = 0; // Default fallback
        if (propertyId) {
          const unitData = unitInstallments[propertyId];
          if (unitData?.metadata?.revenue_MID) {
            const revenueMidStr = unitData.metadata.revenue_MID;
            const numValue = Number(revenueMidStr);
            if (
              !isNaN(numValue) &&
              revenueMidStr !== null &&
              revenueMidStr !== ""
            ) {
              revenueMidValue = numValue;
              logger.debug(
                "PaymentSummaryScreen:Using revenue_MID from unit metadata",
                {
                  propertyId,
                  installmentId: p.id,
                  revenue_MID: revenueMidStr,
                  revenue_mid: revenueMidValue,
                },
              );
            } else {
              logger.warn(
                "PaymentSummaryScreen:Invalid revenue_MID in metadata",
                {
                  propertyId,
                  installmentId: p.id,
                  revenue_MID: revenueMidStr,
                },
              );
            }
          } else {
            logger.warn(
              "PaymentSummaryScreen:Missing revenue_MID in unit metadata",
              {
                propertyId,
                installmentId: p.id,
                hasUnitData: !!unitData,
                hasMetadata: !!unitData?.metadata,
              },
            );
          }
        } else {
          logger.warn("PaymentSummaryScreen:Missing propertyId for payment", {
            installmentId: p.id,
            hasOriginalItem: !!p.originalItem,
          });
        }

        return {
          installment_id: p.id,
          mid: Number(p.mid!),
          revenue_mid: revenueMidValue,
          amount: p.amount.toString(),
          payment_type: p.payment_type,
        };
      });

      // Calculate sum of splits (only payments with MID)
      const sumOfSplits = splits.reduce(
        (sum, split) => sum + Number(split.amount),
        0,
      );

      // Recalculate convenience fee ONLY on payments with MID (those in splits)
      // This ensures conv_fee is calculated on the same subset as splits
      const checkoutConvenienceFee = feesData?.convenienceFee
        ? Math.round(sumOfSplits * feesData.convenienceFee * 100) / 100
        : 0;

      // Amount must equal: sum(splits) + conv_fee
      // This ensures validation: sum(splits) = amount - conv_fee
      const checkoutAmount = sumOfSplits + checkoutConvenienceFee;

      // Get TNC version - prefer Contentful (source of truth for copy), fallback to backend
      const tncVersion = contentfulTNCVersion || backendTNCVersion;

      logger.debug("PaymentSummaryScreen:TNC version for checkout", {
        contentfulTNCVersion,
        backendTNCVersion,
        tncVersion,
        willInclude: !!tncVersion,
      });

      // Check if any splits have missing revenue_mid (value 0 indicates fallback was used)
      const splitsWithMissingRevenueMid = splits.filter(
        (s) => s.revenue_mid === 0,
      );
      if (splitsWithMissingRevenueMid.length > 0) {
        logger.warn(
          "PaymentSummaryScreen:Some splits have missing revenue_mid",
          {
            count: splitsWithMissingRevenueMid.length,
            splitIds: splitsWithMissingRevenueMid.map((s) => s.installment_id),
          },
        );
        trackDynatraceEvent({
          p_screen: "Payment Summary",
          p_actionName: "Revenue MID Missing",
          p_sectionName: "Payment Flow",
          eventType: "error",
          p_failedMessage: `revenue_MID is missing in unit installments metadata for ${splitsWithMissingRevenueMid.length} split(s)`,
          totalAmount: checkoutAmount,
          paymentCount: selectedPayments.length,
        });
      }

      // Create checkout request (full payload sent to backend)
      const checkoutRequest: CreateCheckoutRequest = {
        total_amount: checkoutAmount,
        description: `Payment for ${paymentsWithMid.length} item(s)`,
        line_type: "property_payment" as const,
        splits,
      };

      // Always include tnc_version if available (prefer backend, fallback to Contentful)
      if (tncVersion) {
        checkoutRequest.tnc_version = tncVersion;
      }

      // Build canonical signing payload to match backend implementation exactly.
      // NOTE: Do NOT include customer_id or conv_fee in the HMAC payload.
      //
      // This mirrors the backend DataWeave script:
      // - All scalar fields are cast to String
      // - Splits are normalized and each field is cast to String
      // - Field order MUST match backend exactly for HMAC verification
      //
      // CRITICAL: We manually construct the JSON string to guarantee field order.
      // JSON.stringify doesn't guarantee order, which causes HMAC mismatches.
      
      // Normalize splits
      const normalizedSplits = splits.map((s) => ({
        installment_id: String(s.installment_id),
        mid: String(s.mid),
        revenue_mid: String(s.revenue_mid),
        amount: String(s.amount),
        payment_type: String(s.payment_type),
      }));

      // Build JSON string manually with exact field order expected by backend
      let signingJson = "{";
      
      // Add tnc_version first if present (backend expects this first)
      if (checkoutRequest.tnc_version) {
        signingJson += `"tnc_version":"${String(checkoutRequest.tnc_version)}",`;
      }
      
      signingJson += `"total_amount":"${String(checkoutRequest.total_amount)}",`;
      signingJson += `"description":"${String(checkoutRequest.description ?? "")}",`;
      signingJson += `"line_type":"${String(checkoutRequest.line_type)}",`;
      signingJson += `"splits":[`;
      
      signingJson += normalizedSplits
        .map(
          (s) =>
            `{"installment_id":"${s.installment_id}","mid":"${s.mid}","revenue_mid":"${s.revenue_mid}","amount":"${s.amount}","payment_type":"${s.payment_type}"}`
        )
        .join(",");
      
      signingJson += "]}";

      // Remove any whitespace (though manual construction shouldn't have any)
      signingJson = signingJson.replace(/(\r|\n| )/g, "");
      const hmacSignature = await generatePaymentHmacSignature(signingJson);

      logger.debug("PaymentSummaryScreen:Creating checkout with HMAC header", {
        hasHmacSignature: !!hmacSignature,
        signingJson,
        signingJsonLength: signingJson.length,
        hmacSignature,
        checkoutRequest,
      });

      const checkoutResponse = await paymentService.createCheckout(
        checkoutRequest,
        hmacSignature,
      );
      logger.debug("PaymentSummaryScreen:Checkout created", checkoutResponse);

      // Track payment checkout initiation event
      // Extract property IDs from selectedPayments originalItem if available
      const uniquePropertyIds = selectedPayments
        .map(
          (p) =>
            (p.originalItem as any)?.propertyId ||
            (p.originalItem as any)?.property_id ||
            "",
        )
        .filter(Boolean);
      const uniquePropertyIdsSet = [...new Set(uniquePropertyIds)];
      trackDynatraceEvent({
        p_screen: "Payment Summary",
        p_actionName: "Payment Checkout Initiated",
        p_sectionName: "Payment Flow",
        eventType: "action",
        orderRef: checkoutResponse.orderRef,
        totalAmount: checkoutAmount,
        paymentCount: selectedPayments.length,
        convenienceFee: checkoutConvenienceFee,
        propertyCount: uniquePropertyIdsSet.length,
        propertyIds:
          uniquePropertyIdsSet.length > 0 ? uniquePropertyIdsSet.join(",") : "",
      });
      // Store orderRef (you may want to store this in appStore or secure storage)
      // For now, we'll navigate to a checkout WebView screen
      router.push({
        pathname: "/(auth)/payment-checkout",
        params: {
          checkoutUrl: checkoutResponse.checkoutUrl,
          orderRef: checkoutResponse.orderRef,
        },
      } as any);
    } catch (error: any) {
      logger.error("PaymentSummaryScreen:Checkout failed", error);

      // Calculate amount for error tracking (recalculate if needed)
      const errorTotalAmount = selectedPayments.reduce(
        (sum, p) => sum + p.amount,
        0,
      );

      // Track payment checkout failure event
      trackDynatraceEvent({
        p_screen: "Payment Summary",
        p_actionName: "Payment Checkout Failed",
        p_sectionName: "Payment Flow",
        eventType: "error",
        p_failedMessage:
          error?.response?.data?.message ||
          error?.message ||
          "Failed to create checkout",
        totalAmount: errorTotalAmount,
        paymentCount: selectedPayments.length,
      });
      setErrorDialogMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to create checkout. Please try again.",
      );
      setErrorDialogVisible(true);
    } finally {
      setIsProcessingCheckout(false);
    }
  };

  // Fetch and compare TNC versions on mount
  useEffect(() => {
    const fetchAndCompareTNCVersions = async () => {
      try {
        setIsLoadingTNCVersions(true);

        // Fetch TNC version from Contentful
        let contentfulVersion: number | null = null;
        try {
          contentfulVersion = await fetchTNCVersion();
          if (contentfulVersion !== null) {
            setContentfulTNCVersion(String(contentfulVersion));
          }
        } catch (error) {
          logger.error(
            "PaymentSummaryScreen:Failed to fetch Contentful TNC version",
            error,
          );
        }

        // Fetch TNC version from backend
        let backendVersion: string | null = null;
        try {
          const tncResponse = await paymentService.getTNCVersion();
          logger.debug("PaymentSummaryScreen:TNC version API response", {
            success: tncResponse?.success,
            data: tncResponse?.data,
            payment_tnc_version: tncResponse?.data?.payment_tnc_version,
          });
          if (tncResponse?.success && tncResponse?.data?.payment_tnc_version) {
            backendVersion = tncResponse.data.payment_tnc_version;
            setBackendTNCVersion(backendVersion);
            logger.info("PaymentSummaryScreen:Backend TNC version set", {
              backendVersion,
            });
          } else {
            logger.warn(
              "PaymentSummaryScreen:Backend TNC version not found in response",
              {
                response: tncResponse,
              },
            );
          }
        } catch (error) {
          logger.error(
            "PaymentSummaryScreen:Failed to fetch backend TNC version",
            error,
          );
        }

        // Compare versions and pre-check if they match
        if (contentfulVersion !== null && backendVersion !== null) {
          const contentfulVersionStr = String(contentfulVersion);
          if (contentfulVersionStr === backendVersion) {
            logger.info(
              "PaymentSummaryScreen:TNC versions match, pre-checking checkbox",
              {
                contentful: contentfulVersionStr,
                backend: backendVersion,
              },
            );
            setIsTermsAccepted(true);
          } else {
            logger.info("PaymentSummaryScreen:TNC versions do not match", {
              contentful: contentfulVersionStr,
              backend: backendVersion,
            });
            setIsTermsAccepted(false);
          }
        } else {
          // If either fetch failed, don't pre-check (safe default)
          logger.warn(
            "PaymentSummaryScreen:Could not compare TNC versions, one or both failed",
            {
              contentful: contentfulVersion,
              backend: backendVersion,
            },
          );
          setIsTermsAccepted(false);
        }
      } catch (error) {
        logger.error(
          "PaymentSummaryScreen:Error in TNC version comparison",
          error,
        );
        setIsTermsAccepted(false);
      } finally {
        setIsLoadingTNCVersions(false);
      }
    };

    void fetchAndCompareTNCVersions();
  }, []);

  const handleTermsPress = () => {
    router.push("/terms-and-conditions?from=payment-summary");
  };

  const handleCheckboxPress = () => {
    if (isTermsAccepted) {
      // If already checked, uncheck it
      setIsTermsAccepted(false);
    } else {
      // If unchecked, navigate to TNC screen
      router.push("/terms-and-conditions?from=payment-summary");
    }
  };

  // Handle TNC acceptance callback (called from LegalDocumentScreen)
  useEffect(() => {
    if (params.tncAccepted === "true") {
      setIsTermsAccepted(true);
      // Clear the param to avoid re-triggering
      router.setParams({ tncAccepted: undefined } as any);
    }
  }, [params.tncAccepted]);

  const styles = StyleSheet.create({
    container: {
      flex: 1,
    },
    scrollView: {
      flex: 1,
    },
    scrollContent: {
      flexGrow: 1,
      paddingBottom: 24,
    },
    content: {
      paddingHorizontal: 16,
      paddingTop: 16,
      gap: 24,
      alignItems: "center",
    },
    sectionWrapper: {
      width: "100%",
      maxWidth: 600,
      alignItems: "center",
    },
    section: {
      gap: 8,
      width: "100%",
    },
    sectionTitle: {
      fontSize: 14,
      fontWeight: "500",
      paddingHorizontal: 8,
    },
    mainCard: {
      borderRadius: 12,
      paddingHorizontal: 16,
      paddingTop: 20,
      paddingBottom: 8,
      gap: 16,
    },
    propertyPaymentsSection: {
      gap: 16,
    },
    feesSection: {
      gap: 16,
    },
    subsectionTitle: {
      fontSize: 14,
      fontWeight: "500",
    },
    paymentsList: {
      gap: 20,
    },
    paymentItem: {
      gap: 4,
    },
    propertyName: {
      fontSize: 12,
      fontWeight: "400",
    },
    paymentRow: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
    },
    paymentTitle: {
      flex: 1,
      fontSize: 16,
      fontWeight: "600",
    },
    amountContainer: {
      flexDirection: "row",
      alignItems: "center",
      gap: 4,
    },
    amount: {
      fontSize: 16,
      fontWeight: "600",
    },
    dueDate: {
      fontSize: 12,
      fontWeight: "400",
    },
    feesList: {
      gap: 16,
    },
    feeItem: {
      gap: 4,
    },
    feeRow: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
    },
    feeLeft: {
      flex: 1,
      flexDirection: "row",
      alignItems: "center",
      gap: 4,
    },
    feeTitle: {
      fontSize: 13,
      fontWeight: "400",
    },
    feeAmount: {
      fontSize: 13,
      fontWeight: "400",
    },
    totalSection: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
    },
    totalLeft: {
      flex: 1,
      gap: 4,
    },
    totalLabel: {
      fontSize: 16,
      fontWeight: "600",
    },
    totalNote: {
      fontSize: 12,
      fontWeight: "400",
    },
    totalAmount: {
      fontSize: 16,
      fontWeight: "600",
    },
    cardFeeSection: {
      borderRadius: 8,
      paddingHorizontal: 8,
      paddingVertical: 12,
      gap: 12,
      marginHorizontal: -8,
    },
    cardFeeTitle: {
      fontSize: 14,
      fontWeight: "500",
      width: "100%",
    },
    cardFeeOptions: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: 8,
    },
    cardFeeOption: {
      borderRadius: 8,
      padding: 0,
      gap: 8,
    },
    cardFeeLabel: {
      fontSize: 13,
      fontWeight: "400",
    },
    cardFeeAmount: {
      fontSize: 14,
      fontWeight: "500",
    },
    termsSection: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
      paddingTop: 8,
      width: "100%",
      maxWidth: 600,
    },
    buttonContainer: {
      width: "100%",
      maxWidth: 600,
      paddingTop: 8,
    },
    checkboxContainer: {
      // Removed paddingTop to center with text
    },
    termsText: {
      flex: 1,
    },
    termsTextContent: {
      fontSize: 15,
      lineHeight: 20,
      includeFontPadding: false,
      textAlignVertical: "center",
    },
    termsTextRegular: {
      fontSize: 15,
      lineHeight: 20,
      color: colors.fontSecondary,
      fontFamily: "Matter",
      fontWeight: "400",
      includeFontPadding: false,
      textAlignVertical: "top",
    },
    termsLink: {
      fontSize: 15,
      lineHeight: 20,
      color: colors.fontPrimary,
      fontFamily: "Matter-SemiBold",
      fontWeight: "600",
      textDecorationLine: "underline",
      includeFontPadding: false,
      textAlignVertical: "top",
    },
    bottomSection: {
      width: "100%",
      paddingHorizontal: 16,
      paddingTop: 12,
      borderTopWidth: 1,
      backgroundColor: "transparent",
    },
    payNowButton: {
      width: "100%",
    },
    emptyState: {
      textAlign: "center",
      paddingVertical: 24,
    },
  });

  return (
    <View
      style={[styles.container, { backgroundColor: colors.backgroundLevel2 }]}
    >
      <TextHeader
        title={t("payments.paymentSummary")}
        onBackPress={handleBackPress}
        showSupportButton={true}
        onSupportPress={handleSupportPress}
      />

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.content}>
          {/* Centered Payment Breakdown Section */}
          <View style={styles.sectionWrapper}>
            <View style={styles.section}>
              <Typography
                variant="subhead"
                color="primary"
                style={[
                  styles.sectionTitle,
                  { textAlign: isRTL ? "right" : "left" },
                ]}
              >
                {t("payments.paymentBreakdown")}
              </Typography>

              {/* Main Card - Single card containing all sections */}
              <View
                style={[
                  styles.mainCard,
                  { backgroundColor: colors.backgroundLevel1 },
                ]}
              >
                {/* Property Payments Section - Only show if there are installments */}
                {installments.length > 0 && (
                  <>
                    <View style={styles.propertyPaymentsSection}>
                      <Typography
                        variant="subhead"
                        color="secondary"
                        style={[
                          styles.subsectionTitle,
                          { textAlign: isRTL ? "right" : "left" },
                        ]}
                      >
                        {t("payments.propertyPayments")}
                      </Typography>

                      <View style={styles.paymentsList}>
                        {installments.map((payment: any) => (
                          <View key={payment.id} style={styles.paymentItem}>
                            <Typography
                              variant="caption"
                              color="tertiary"
                              style={[
                                styles.propertyName,
                                { textAlign: isRTL ? "right" : "left" },
                              ]}
                            >
                              {payment.propertyName}
                            </Typography>

                            <View style={styles.paymentRow}>
                              <Typography
                                variant="headline"
                                color="secondary"
                                style={[
                                  styles.paymentTitle,
                                  { textAlign: isRTL ? "right" : "left" },
                                ]}
                              >
                                {payment.title}
                              </Typography>
                              <View
                                style={[
                                  styles.amountContainer,
                                  { direction: "ltr" },
                                ]}
                              >
                                <DirhamIcon
                                  size={13}
                                  color={colors.fontPrimary}
                                />
                                <Typography
                                  variant="headline"
                                  color="primary"
                                  style={styles.amount}
                                >
                                  {payment.amount.toLocaleString()}
                                </Typography>
                              </View>
                            </View>

                            <Typography
                              variant="caption"
                              color="tertiary"
                              style={[
                                styles.dueDate,
                                { textAlign: isRTL ? "right" : "left" },
                              ]}
                            >
                              {payment.dueDate}
                            </Typography>
                          </View>
                        ))}
                      </View>
                    </View>

                    {/* Divider - Property Payments to Fees - Only show if there are installments */}
                    <Divider />
                  </>
                )}

                {/* Fees and Charges Section */}
                <View style={styles.feesSection}>
                  <Typography
                    variant="subhead"
                    color="secondary"
                    style={[
                      styles.subsectionTitle,
                      { textAlign: isRTL ? "right" : "left" },
                    ]}
                  >
                    {t("payments.feesAndCharges")}
                  </Typography>

                  <View style={styles.feesList}>
                    {/* Other Charges (other_charge type) */}
                    {charges.map((charge: any) => (
                      <View key={charge.id} style={styles.feeItem}>
                        <Typography
                          variant="caption"
                          color="tertiary"
                          style={[
                            styles.propertyName,
                            { textAlign: isRTL ? "right" : "left" },
                          ]}
                        >
                          {charge.propertyName}
                        </Typography>
                        <View style={styles.feeRow}>
                          <View style={styles.feeLeft}>
                            <Typography
                              variant="footnote"
                              color="secondary"
                              style={[
                                styles.feeTitle,
                                { textAlign: isRTL ? "right" : "left" },
                              ]}
                            >
                              {charge.title}
                            </Typography>
                          </View>
                          <View
                            style={[
                              styles.amountContainer,
                              { direction: "ltr" },
                            ]}
                          >
                            <DirhamIcon size={11} color={colors.fontPrimary} />
                            <Typography
                              variant="footnote"
                              color="primary"
                              style={styles.feeAmount}
                            >
                              {charge.amount.toLocaleString()}
                            </Typography>
                          </View>
                        </View>
                      </View>
                    ))}

                    {/* Late Payment Fee - Only show if > 0 */}
                    {latePaymentFee > 0 && (
                      <View style={styles.feeItem}>
                        <Typography
                          variant="caption"
                          color="tertiary"
                          style={styles.propertyName}
                        >
                          {installments[0]?.propertyName ||
                            charges[0]?.propertyName ||
                            ""}
                        </Typography>
                        <View style={styles.feeRow}>
                          <View style={styles.feeLeft}>
                            <Typography
                              variant="footnote"
                              color="secondary"
                              style={styles.feeTitle}
                            >
                              {t("payments.latePaymentFee")}
                            </Typography>
                          </View>
                          <View
                            style={[
                              styles.amountContainer,
                              { direction: "ltr" },
                            ]}
                          >
                            <DirhamIcon size={11} color={colors.fontPrimary} />
                            <Typography
                              variant="footnote"
                              color="primary"
                              style={styles.feeAmount}
                            >
                              {latePaymentFee.toLocaleString()}
                            </Typography>
                          </View>
                        </View>
                      </View>
                    )}

                    {/* Convenience Fee - Always show */}
                    <View style={styles.feeItem}>
                        <View style={styles.feeRow}>
                        <View style={styles.feeLeft}>
                          <Typography
                            variant="footnote"
                            color="secondary"
                            style={styles.feeTitle}
                          >
                            {t("payments.convenienceFee")}
                          </Typography>
                        </View>
                        <View
                          style={[styles.amountContainer, { direction: "ltr" }]}
                        >
                          <DirhamIcon size={11} color={colors.fontPrimary} />
                          <Typography
                            variant="footnote"
                            color="primary"
                            style={styles.feeAmount}
                          >
                            {convenienceFee.toLocaleString()}
                          </Typography>
                        </View>
                      </View>
                    </View>

                    {/* Show loading state for fees */}
                    {isLoadingFees && charges.length === 0 && (
                      <>
                        <View style={styles.feeItem}>
                          <Skeleton
                            width={100}
                            height={12}
                            style={{ marginBottom: 4 }}
                          />
                          <View style={styles.feeRow}>
                            <View style={styles.feeLeft}>
                              <Skeleton width={120} height={13} />
                            </View>
                            <View style={styles.amountContainer}>
                              <Skeleton
                                width={10}
                                height={10}
                                variant="circular"
                              />
                              <Skeleton
                                width={60}
                                height={13}
                                style={{ marginLeft: 4 }}
                              />
                            </View>
                          </View>
                        </View>
                        <View style={styles.feeItem}>
                          <Skeleton
                            width={100}
                            height={12}
                            style={{ marginBottom: 4 }}
                          />
                          <View style={styles.feeRow}>
                            <View style={styles.feeLeft}>
                              <Skeleton width={100} height={13} />
                            </View>
                            <View style={styles.amountContainer}>
                              <Skeleton
                                width={10}
                                height={10}
                                variant="circular"
                              />
                              <Skeleton
                                width={50}
                                height={13}
                                style={{ marginLeft: 4 }}
                              />
                            </View>
                          </View>
                        </View>
                      </>
                    )}
                  </View>
                </View>

                {/* Divider - Fees to Total */}
                <Divider />

                {/* Total Section */}
                <View style={styles.totalSection}>
                  <View style={styles.totalLeft}>
                    {isLoadingFees ? (
                      <>
                        <Skeleton
                          width={60}
                          height={16}
                          style={{ marginBottom: 4 }}
                        />
                        <Skeleton width={180} height={12} />
                      </>
                    ) : (
                      <>
                        <Typography
                          variant="headline"
                          color="primary"
                          style={[
                            styles.totalLabel,
                            { textAlign: isRTL ? "right" : "left" },
                          ]}
                        >
                          {t("payments.total")}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="tertiary"
                          style={[
                            styles.totalNote,
                            { textAlign: isRTL ? "right" : "left" },
                          ]}
                        >
                          {t("payments.excludingCardProcessingFee")}
                        </Typography>
                      </>
                    )}
                  </View>
                  <View style={[styles.amountContainer, { direction: "ltr" }]}>
                    {isLoadingFees ? (
                      <>
                        <Skeleton width={12} height={12} variant="circular" />
                        <Skeleton
                          width={80}
                          height={16}
                          style={{ marginLeft: 4 }}
                        />
                      </>
                    ) : (
                      <>
                        <DirhamIcon size={14} color={colors.fontPrimary} />
                        <Typography
                          variant="headline"
                          color="primary"
                          style={styles.totalAmount}
                        >
                          {totalBeforeCardFee.toLocaleString()}
                        </Typography>
                      </>
                    )}
                  </View>
                </View>

                {/* Estimated Card Processing Fee Section */}
                {/* TODO: Add card processing fee section later, commented temporarily  */}
                {/* <View style={[styles.cardFeeSection, { backgroundColor: colors.backgroundLevel2 }]}>
                                <Typography variant="subhead" color="primary" style={styles.cardFeeTitle}>
                                    {t('payments.estimatedCardProcessingFee')}
                                </Typography>

                                <View style={styles.cardFeeOptions}>
                                    {isLoadingFees ? (
                                        <>
                                            <View style={[styles.cardFeeOption, { backgroundColor: colors.backgroundLevel2 }]}>
                                                <Skeleton width={150} height={13} />
                                                <View style={styles.amountContainer}>
                                                    <Skeleton width={10} height={10} variant="circular" />
                                                    <Skeleton width={60} height={14} style={{ marginLeft: 4 }} />
                                                </View>
                                            </View>
                                            <View style={[styles.cardFeeOption, { backgroundColor: colors.backgroundLevel2 }]}>
                                                <Skeleton width={160} height={13} />
                                                <View style={styles.amountContainer}>
                                                    <Skeleton width={10} height={10} variant="circular" />
                                                    <Skeleton width={60} height={14} style={{ marginLeft: 4 }} />
                                                </View>
                                            </View>
                                        </>
                                    ) : (
                                        <>
                                            <View style={[styles.cardFeeOption, { backgroundColor: colors.backgroundLevel2 }]}>
                                                <Typography variant="footnote" color="secondary" style={styles.cardFeeLabel}>
                                                    {t('payments.domesticCards')} ({domesticRate > 0 ? `${(domesticRate * 100).toFixed(2)}%` : '0%'})
                                                </Typography>
                                                <View style={[styles.amountContainer, { direction: 'ltr' }]}>
                                                    <DirhamIcon size={10} color={colors.fontSecondary} />
                                                    <Typography variant="subhead" color="secondary" style={styles.cardFeeAmount}>
                                                        {domesticCardFee.toLocaleString()}
                                                    </Typography>
                                                </View>
                                            </View>

                                            <View style={[styles.cardFeeOption, { backgroundColor: colors.backgroundLevel2 }]}>
                                                <Typography variant="footnote" color="secondary" style={styles.cardFeeLabel}>
                                                    {t('payments.internationalCards')} ({internationalRate > 0 ? `${(internationalRate * 100).toFixed(2)}%` : '0%'})
                                                </Typography>
                                                <View style={[styles.amountContainer, { direction: 'ltr' }]}>
                                                    <DirhamIcon size={10} color={colors.fontSecondary} />
                                                    <Typography variant="subhead" color="secondary" style={styles.cardFeeAmount}>
                                                        {internationalCardFee.toLocaleString()}
                                                    </Typography>
                                                </View>
                                            </View>
                                        </>
                                    )}
                                </View>
                            </View> */}
              </View>
            </View>
          </View>

          {/* On web: terms + Next inside scroll (desktop position unchanged) */}
          {Platform.OS === "web" && (
            <>
              <View style={styles.termsSection}>
                <View style={styles.checkboxContainer}>
                  {isLoadingTNCVersions ? (
                    <Skeleton width={24} height={24} borderRadius={4} />
                  ) : (
                    <Checkbox
                      isSelected={isTermsAccepted}
                      onPress={handleCheckboxPress}
                      size={24}
                    />
                  )}
                </View>
                <View style={styles.termsText}>
                  {isLoadingTNCVersions ? (
                    <Skeleton width="100%" height={20} />
                  ) : (
                    <RNText style={styles.termsTextContent}>
                      <RNText style={styles.termsTextRegular}>
                        {t("registration.termsPrefix")}{" "}
                      </RNText>
                      <RNText style={styles.termsLink} onPress={handleTermsPress}>
                        {t("registration.termsLink")}
                      </RNText>
                    </RNText>
                  )}
                </View>
              </View>
              <View
                style={[
                  styles.buttonContainer,
                  { paddingBottom: insets.bottom + 12 },
                ]}
              >
                <Button
                  title={t("common.next")}
                  variant="primary"
                  onPress={handlePayNowPress}
                  style={styles.payNowButton}
                  disabled={
                    isProcessingCheckout ||
                    selectedPayments.length === 0 ||
                    !isTermsAccepted
                  }
                />
              </View>
            </>
          )}
        </View>
      </ScrollView>

      {/* On native: fixed bottom section so taps work on iOS/Android */}
      {Platform.OS !== "web" && (
        <View
          style={[
            styles.bottomSection,
            {
              borderColor: colors.outlineInactive,
              backgroundColor: colors.backgroundLevel2,
              paddingBottom: insets.bottom + 12,
            },
          ]}
        >
          <View style={styles.termsSection}>
            <View style={styles.checkboxContainer}>
              {isLoadingTNCVersions ? (
                <Skeleton width={24} height={24} borderRadius={4} />
              ) : (
                <Checkbox
                  isSelected={isTermsAccepted}
                  onPress={handleCheckboxPress}
                  size={24}
                />
              )}
            </View>
            <View style={styles.termsText}>
              {isLoadingTNCVersions ? (
                <Skeleton width="100%" height={20} />
              ) : (
                <RNText style={styles.termsTextContent}>
                  <RNText style={styles.termsTextRegular}>
                    {t("registration.termsPrefix")}{" "}
                  </RNText>
                  <RNText style={styles.termsLink} onPress={handleTermsPress}>
                    {t("registration.termsLink")}
                  </RNText>
                </RNText>
              )}
            </View>
          </View>
          <View style={styles.buttonContainer}>
            <Button
              title={t("common.next")}
              variant="primary"
              onPress={handlePayNowPress}
              style={styles.payNowButton}
              disabled={
                isProcessingCheckout ||
                selectedPayments.length === 0 ||
                !isTermsAccepted
              }
            />
          </View>
        </View>
      )}

      {/* Error Dialog */}
      <OkDialog
        visible={errorDialogVisible}
        message={errorDialogMessage}
        onOk={() => setErrorDialogVisible(false)}
      />
      <FullScreenLoader visible={isProcessingCheckout} />
    </View>
  );
}
